import { Link } from 'react-router-dom'
import { ArrowRight, ClipboardList, CreditCard, ListChecks, Receipt } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { OpenRequestsPanel } from '@/components/dashboard/open-requests-panel'
import { Badge } from '@/components/ui/badge'
import { useApprovals } from '@/hooks/use-approvals'
import { useMemberArrears } from '@/hooks/use-billing'
import { useUnmatchedC2BTransactions } from '@/hooks/use-payments'
import { useServiceRequests } from '@/hooks/use-requests'
import type { ApprovalStatus } from '@riverpark/types'

const RECENT_LIMIT = 5

function formatDateTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function statusBadgeVariant(status: ApprovalStatus): 'pending' | 'paid' | 'overdue' {
  if (status === 'pending') return 'pending'
  if (status === 'approved') return 'paid'
  return 'overdue' // rejected or failed
}

function StatCard({
  to,
  icon: Icon,
  label,
  value,
}: {
  to: string
  icon: typeof ClipboardList
  label: string
  value: string
}) {
  return (
    <Link
      to={to}
      className="rounded-card border border-border-2 bg-card p-4 transition-colors hover:border-green/40"
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={15} />
        <p className="font-sans text-[11px] font-medium uppercase tracking-[0.1em]">{label}</p>
      </div>
      <p className="mt-2 font-serif text-2xl text-ink">{value}</p>
    </Link>
  )
}

export function OperationsDashboardPage() {
  const { data: requestsData, isLoading: requestsLoading } = useServiceRequests({ limit: 100 })
  const { data: arrearsData } = useMemberArrears({ limit: 1 })
  const { data: unmatchedData } = useUnmatchedC2BTransactions()
  const { data: approvalsData, isLoading: approvalsLoading } = useApprovals()

  const openRequests = (requestsData?.data ?? []).filter(
    (r) => r.status === 'open' || r.status === 'in_progress',
  )
  const arrearsTotal = arrearsData?.meta.total ?? 0
  const unmatchedCount = unmatchedData?.data.length ?? 0
  const recentRequests = (approvalsData?.data ?? []).slice(0, RECENT_LIMIT)

  return (
    <PageShell>
      <PageHeader label="Overview" title="Dashboard" description="Today, at a glance." />

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <StatCard
          to="/operations/requests"
          icon={ClipboardList}
          label="Open requests"
          value={String(openRequests.length)}
        />
        <StatCard
          to="/operations/billing"
          icon={Receipt}
          label="Members in arrears"
          value={String(arrearsTotal)}
        />
        <StatCard
          to="/operations/payments"
          icon={CreditCard}
          label="Unmatched payments"
          value={String(unmatchedCount)}
        />
      </div>

      <OpenRequestsPanel
        basePath="/operations"
        requests={openRequests}
        isLoading={requestsLoading}
      />

      <div className="rounded-card border border-border-2 bg-card">
        <div className="flex items-center justify-between gap-2 border-b border-border-2 px-5 py-3">
          <div className="flex items-center gap-2">
            <ListChecks size={16} className="text-muted-foreground" />
            <h2 className="font-serif text-lg text-ink">Your requests</h2>
          </div>
          <Link
            to="/operations/approvals"
            className="flex items-center gap-1 font-sans text-xs font-medium text-muted-foreground hover:text-primary"
          >
            View all
            <ArrowRight size={12} />
          </Link>
        </div>

        {approvalsLoading ? (
          <p className="px-5 py-6 font-sans text-sm text-muted-foreground">Loading…</p>
        ) : recentRequests.length === 0 ? (
          <p className="px-5 py-6 font-sans text-sm text-muted-foreground">
            Nothing here yet — actions that need committee/admin sign-off will show up here.
          </p>
        ) : (
          <ul className="divide-y divide-border-2">
            {recentRequests.map((r) => (
              <li key={r.id} className="px-5 py-3.5">
                <div className="flex items-center gap-2">
                  <Badge variant={statusBadgeVariant(r.status)}>{r.status}</Badge>
                  <p className="truncate font-sans text-sm font-medium text-ink">{r.summary}</p>
                </div>
                <p className="mt-1 font-sans text-xs text-muted-foreground">
                  Requested {formatDateTime(r.createdAt)}
                  {r.reviewedByName && <> · Decided by {r.reviewedByName}</>}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageShell>
  )
}
